import { ErrorIcon, MailIcon, SuccessIcon } from '@bi-digital/icons';
import type { ButtonProps } from '@bi-digital/react';
import {
    Button,
    Dialog,
    DialogCloseButton,
    DialogContent,
    DialogFooter,
    DialogHeader,
    useMediaQuery,
} from '@bi-digital/react';
import { fetcher } from '@bino/utils/fetcher';
import type { UserData } from '@bino/utils/functions/ga';
import { trackGAEvent } from '@bino/utils/functions/ga';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useProductPageContext } from '../../../context/ProductPageContext';
import { wciaFormSchema, type WciaFormValues, type WciaRequest } from './wciaSchema';
import { WciaForm } from './WciaForm/WciaForm';
import styles from './WhenCanIApply.module.css';

const trackWciaSignupToGA = (
    data: Omit<WciaRequest, 'firstName' | 'lastName' | 'phoneNumber' | 'email'> & { programme: string },
    userData: UserData,
) => trackGAEvent({ event: 'WCIA Signup', data, userData });

export type WhenCanIApplyProps = Omit<ButtonProps, 'onClick'> & {
    programmeId: number;
};

export default function WhenCanIApply({ programmeId, ...buttonProps }: WhenCanIApplyProps) {
    const { programme } = useProductPageContext();
    const { t, i18n } = useTranslation();
    const isLargeScreen = useMediaQuery('(width >= 1024px)');
    const [status, setStatus] = useState<'success' | 'error' | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const language = i18n.language as 'no' | 'en';

    const { handleSubmit, formState, register } = useForm<WciaFormValues>({
        defaultValues: {
            phoneCountryCode: '+47',
        },
        resolver: zodResolver(wciaFormSchema),
    });

    function closeDialog() {
        setDialogOpen(false);
        setStatus(null);
    }

    const submitForm: SubmitHandler<WciaFormValues> = async ({
        firstName,
        lastName,
        phoneCountryCode,
        phoneNumber,
        email,
        consent,
    }) => {
        const form: WciaRequest = {
            firstName,
            lastName,
            phoneCountryCode,
            phoneNumber,
            email,
            consent,
            bannerProductId: String(programmeId),
            language,
        };

        try {
            trackWciaSignupToGA(
                {
                    language: form.language,
                    programme: programme.name,
                    phoneCountryCode: form.phoneCountryCode,
                    consent: form.consent,
                    bannerProductId: String(programmeId),
                },
                {
                    email: form.email,
                    phone: form.phoneNumber,
                },
            );
            await fetcher.post(`/api/when-can-i-apply`, form);
            setStatus('success');
        } catch {
            setStatus('error');
        }
    };

    return (
        <>
            <Button onClick={() => setDialogOpen(true)} startIcon={<MailIcon aria-hidden='true' />} {...buttonProps}>
                {t('cart:whencaniapply')}
            </Button>
            <Dialog
                anchor={isLargeScreen ? undefined : 'bottom'}
                closedby='any'
                className={styles.dialog}
                open={dialogOpen}
                onClose={closeDialog}
                data-color-scheme='light'
                data-color='neutral'
            >
                <DialogCloseButton aria-label='Close dialog' onClick={closeDialog} />
                {status ? (
                    <DialogContent className={styles.resultContent}>
                        {status === 'success' ? (
                            <SuccessIcon size='2xl' fill='var(--bi-primary-50)' />
                        ) : (
                            <ErrorIcon size='2xl' fill='var(--bi-alert-60)' />
                        )}
                        <p className={styles.resultMessage}>
                            {status === 'success' ? t('cart:wcia.successMessage') : t('cart:wcia.errorMessage')}
                        </p>
                        <Button fullwidth className={styles.submitButton} variant='outlined' onClick={closeDialog}>
                            {t('common:close')}
                        </Button>
                    </DialogContent>
                ) : (
                    <>
                        <DialogHeader noBorder={isLargeScreen} className={styles.dialogHeader}>
                            {t('cart:wcia.dialogTitle')}
                        </DialogHeader>
                        <DialogContent noBorder={isLargeScreen} className={styles.dialogContent}>
                            <p className={styles.introText}>{t('cart:wcia.dialogIntro')}</p>
                            <WciaForm formState={formState} register={register} onSubmit={handleSubmit(submitForm)} />
                        </DialogContent>
                        {!isLargeScreen && (
                            <DialogFooter>
                                <Button
                                    fullwidth
                                    size='large'
                                    className={styles.submitButton}
                                    submitting={formState.isSubmitting}
                                    disabled={formState.isSubmitting}
                                    type='submit'
                                    form='wciaForm'
                                >
                                    {t('cart:wcia.submit')}
                                </Button>
                            </DialogFooter>
                        )}
                    </>
                )}
            </Dialog>
        </>
    );
}
